import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import Sidebar from './components/Sidebar';
import ProductGrid from './components/ProductGrid';
import SearchBar from './components/SearchBar';
import Pagination from './components/Pagination';
import RecommendationSection from './components/RecommendationSection';
import NewsletterCTA from './components/NewsletterCTA';
import Footer from './components/Footer';
import ProductDetailPage from './components/ProductDetailPage';
import CartPage from './components/CartPage';
import FavoritesPage from './components/FavoritesPage';
import { CartProvider, useCart } from './context/CartContext';
import { FavoritesProvider } from './context/FavoritesContext';

import {
  getProducts,
  getCategories,
  getFilters,
  getSortOptions,
  getPriceRanges,
  getRecommendedProducts,
} from './services/productService';

// Home Page Component
function HomePage() {
  const navigate = useNavigate();
  const { cartItemsCount } = useCart();
  
  // Data States
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState([]);
  const [sortOptions, setSortOptions] = useState([]);
  const [priceRanges, setPriceRanges] = useState([]);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filter States
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPriceRange, setSelectedPriceRange] = useState('all');
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('featured');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 6;

  // Mobile Sidebar
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [
          productsData,
          categoriesData,
          filtersData,
          sortOptionsData,
          priceRangesData,
          recommendedData,
        ] = await Promise.all([
          getProducts(),
          getCategories(),
          getFilters(),
          getSortOptions(),
          getPriceRanges(),
          getRecommendedProducts(6),
        ]);

        setProducts(productsData);
        setCategories(categoriesData);
        setFilters(filtersData);
        setSortOptions(sortOptionsData);
        setPriceRanges(priceRangesData);
        setRecommendedProducts(recommendedData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filtered and sorted products using useMemo
  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    // Filter by category
    if (selectedCategory && selectedCategory !== 'all') {
      const categoryName = selectedCategory
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase());
      filtered = filtered.filter(
        (p) =>
          p.category.toLowerCase().replace(/\s+/g, '-') === selectedCategory ||
          p.category === categoryName
      );
    }

    // Filter by price range
    const range = priceRanges.find((r) => r.id === selectedPriceRange);
    if (range && selectedPriceRange !== 'all') {
      filtered = filtered.filter(
        (p) => p.price >= range.min && p.price < range.max
      );
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.category.toLowerCase().includes(query) ||
          p.brandTag.toLowerCase().includes(query)
      );
    }

    // Sort products
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'favorites':
        filtered.sort((a, b) => b.reviews - a.reviews);
        break;
      default:
        // Featured - sort by rating
        filtered.sort((a, b) => b.rating - a.rating);
    }

    return filtered;
  }, [products, selectedCategory, selectedPriceRange, searchQuery, sortBy, priceRanges]);

  // Paginated products
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * productsPerPage;
    const endIndex = startIndex + productsPerPage;
    return filteredProducts.slice(startIndex, endIndex);
  }, [filteredProducts, currentPage, productsPerPage]);

  // Total pages
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedPriceRange, searchQuery, sortBy]);

  // Handlers
  const handleCategoryChange = useCallback((category) => {
    setSelectedCategory(category);
  }, []);

  const handlePriceRangeChange = useCallback((range) => {
    setSelectedPriceRange(range);
  }, []);

  const handleFilterChange = useCallback((filterId) => {
    setSelectedFilters((prev) =>
      prev.includes(filterId)
        ? prev.filter((f) => f !== filterId)
        : [...prev, filterId]
    );
  }, []);

  const handleSearchChange = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  const handleSortChange = useCallback((sort) => {
    setSortBy(sort);
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    // Scroll to top of product grid
    window.scrollTo({ top: 400, behavior: 'smooth' });
  }, []);

  const handleProductClick = useCallback((productId) => {
    navigate(`/product/${productId}`);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <Navbar
        cartCount={cartItemsCount}
        onSearchChange={handleSearchChange}
        searchQuery={searchQuery}
      />

      {/* Hero Section */}
      <HeroSection />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Section Title */}
        <div className="mb-6 md:mb-8">
          <h2 className="heading-2 text-foreground">Give All You Need</h2>
        </div>

        {/* Shop Content */}
        <div className="flex gap-6 lg:gap-8">
          {/* Sidebar */}
          <Sidebar
            categories={categories}
            filters={filters}
            priceRanges={priceRanges}
            selectedCategory={selectedCategory}
            selectedPriceRange={selectedPriceRange}
            selectedFilters={selectedFilters}
            onCategoryChange={handleCategoryChange}
            onPriceRangeChange={handlePriceRangeChange}
            onFilterChange={handleFilterChange}
            isMobileOpen={isMobileSidebarOpen}
            onMobileClose={() => setIsMobileSidebarOpen(false)}
          />

          {/* Product Section */}
          <div className="flex-1 min-w-0">
            {/* Search Bar */}
            <SearchBar
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              sortBy={sortBy}
              onSortChange={handleSortChange}
              sortOptions={sortOptions}
              onFilterClick={() => setIsMobileSidebarOpen(true)}
              totalProducts={filteredProducts.length}
            />

            {/* Product Grid */}
            <ProductGrid 
              products={paginatedProducts} 
              isLoading={isLoading} 
              onProductClick={handleProductClick}
            />

            {/* Pagination */}
            {!isLoading && totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </div>
      </main>

      {/* Recommendations Section */}
      <RecommendationSection products={recommendedProducts} onProductClick={handleProductClick} />

      {/* Newsletter CTA */}
      <NewsletterCTA />

      {/* Footer */}
      <Footer />
    </div>
  );
}

function App() {
  return (
    <Router>
      <CartProvider>
        <FavoritesProvider>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/product/:productId" element={<ProductDetailPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/favorites" element={<FavoritesPage />} />
          </Routes>
        </FavoritesProvider>
      </CartProvider>
    </Router>
  );
}

export default App;
